import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Ruler, Bike, Sparkles, Leaf, Users, HeartHandshake } from "lucide-react";
import HoursWidget from "@/components/HoursWidget";
import Squiggle, { DoodleDot } from "@/components/Squiggle";
import { IMAGES } from "@/config/site";
import { useSettings } from "@/context/SettingsContext";

// Parse "8:00 AM" / "2:30 PM" → minutes-since-midnight, or null if unparseable.
function parseTimeToMinutes(str) {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM|am|pm)?$/);
  if (!m) return null;
  let hh = parseInt(m[1], 10);
  const mm = m[2] ? parseInt(m[2], 10) : 0;
  const ap = (m[3] || "").toUpperCase();
  if (ap === "PM" && hh < 12) hh += 12;
  if (ap === "AM" && hh === 12) hh = 0;
  return hh * 60 + mm;
}

function isOpenNow(hours) {
  if (!Array.isArray(hours)) return false;
  const now = new Date();
  const dayIdx = now.getDay() === 0 ? 6 : now.getDay() - 1; // Mon=0..Sun=6
  const today = hours[dayIdx];
  if (!today || today.closed) return false;
  const openM = parseTimeToMinutes(today.open);
  const closeM = parseTimeToMinutes(today.close);
  if (openM == null || closeM == null) return false; // e.g. "By Appointment"
  const cur = now.getHours() * 60 + now.getMinutes();
  return cur >= openM && cur < closeM;
}

export default function Home() {
  const { settings } = useSettings();
  const IMG = { ...IMAGES, ...(settings.images || {}) };
  const openNow = isOpenNow(settings.hours);
  return (
    <div data-testid="page-home">
      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden px-4 pt-10 sm:px-8 md:px-12 md:pt-14">
        <div className="pointer-events-none absolute -top-20 left-1/2 h-72 w-[90%] -translate-x-1/2 rounded-[50%] bg-sun-200/60 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl gap-10 md:grid-cols-[1.1fr_0.9fr] md:gap-14">
          <div className="fade-up">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border-2 border-leaf-200 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-leaf-800">
              <Leaf className="h-3.5 w-3.5" /> St. Louis · Community Owned
            </div>
            <h1 className="font-display text-[2.75rem] font-black leading-[0.95] tracking-tight text-leaf-900 sm:text-6xl md:text-7xl">
              Your <span className="marker-underline italic text-leaf-600">"Last Stop"</span>{" "}
              for cycles that last.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-leaf-900/80 sm:text-xl">
              Bike fitting, repairs, custom builds, and hands-on classes — for
              kids, parents, commuters, and every rider between. No ego. Just
              good wrench time.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact#booking"
                data-testid="hero-cta-book"
                className="inline-flex items-center gap-2 rounded-full bg-leaf-600 px-7 py-4 text-base font-bold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
              >
                Request Wrench Time <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/repairs"
                data-testid="hero-cta-repairs"
                className="inline-flex items-center gap-2 rounded-full border-2 border-leaf-900 bg-transparent px-7 py-4 text-base font-bold text-leaf-900 transition-all hover:-translate-y-0.5 hover:bg-leaf-900 hover:text-white"
              >
                See Repair Menu
              </Link>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm font-semibold text-leaf-900/70">
              <span className="inline-flex items-center gap-1.5">
                <HeartHandshake className="h-4 w-4 text-leaf-600" />
                Family friendly
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-4 w-4 text-leaf-600" />
                Youth programs
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-leaf-600" />
                Ego-free service
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="relative overflow-hidden rounded-[2rem] border-4 border-white shadow-[0_20px_60px_rgba(21,128,61,0.18)]">
              <img
                src={IMG.hero}
                alt="Last Stop Bike Shop trailer loaded with bikes"
                className="h-[420px] w-full object-cover sm:h-[520px]"
              />
              <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-white/95 p-4 backdrop-blur-md">
                <p className="text-xs font-bold uppercase tracking-widest text-leaf-700">
                  Today
                </p>
                <HoursWidget variant="compact" className="mt-1.5" />
              </div>
            </div>
            <div
              data-testid="hero-walk-in-bubble"
              className={`float-gentle wobble-tilt absolute -right-3 top-6 hidden max-w-[190px] rounded-2xl px-4 py-3 text-center text-[11px] font-black uppercase leading-tight tracking-widest text-white shadow-xl sm:block ${
                openNow ? "bg-leaf-600" : "bg-tomato-500"
              }`}
            >
              {openNow ? (
                <>
                  <span className="mr-1 inline-block h-2 w-2 rounded-full bg-white align-middle animate-pulse" />
                  Open now<br />walk in!
                </>
              ) : (
                <>
                  Walk-ins welcome<br />on weekends
                </>
              )}
            </div>
            {/* Decorative dot accents in the corners */}
            <DoodleDot color="sun" size={18} className="absolute -left-3 -top-3 hidden sm:inline-block" />
            <DoodleDot color="tomato" size={12} className="absolute -bottom-2 left-10 hidden sm:inline-block" />
            <DoodleDot color="green" size={16} className="absolute -bottom-4 right-20 hidden sm:inline-block" />
          </div>
        </div>
      </section>

      {/* Multi-color squiggle divider */}
      <Squiggle className="mt-12 px-4" />

      {/* ---------------- VALUE PILLARS ---------------- */}
      <section className="px-4 py-16 sm:px-8 md:px-12">
        <div className="relative mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          {/* decorative scattered dots in the section */}
          <span className="dot-scatter pointer-events-none absolute inset-0 -m-4 hidden md:block" />
          {[
            {
              icon: Wrench,
              title: "Repairs Done Right",
              desc: "From flat fixes to full overhauls. Quick turnaround, honest pricing, no upselling.",
              to: "/repairs",
              color: "leaf",
              testid: "pillar-repairs",
            },
            {
              icon: Ruler,
              title: "Bike Fitting",
              desc: "Kids, commuters, roadies — we dial every contact point for comfort and power.",
              to: "/fitting",
              color: "sun",
              testid: "pillar-fitting",
            },
            {
              icon: Bike,
              title: "Make it Yours",
              desc: "Revive an old frame or build a full custom cargo e-bike for the whole family.",
              to: "/services",
              color: "sky2",
              testid: "pillar-services",
            },
          ].map((p) => (
            <Link
              key={p.title}
              to={p.to}
              data-testid={p.testid}
              className="group relative overflow-hidden rounded-3xl border-2 border-transparent bg-white p-7 transition-all hover:-translate-y-1 hover:border-leaf-300 hover:shadow-xl"
            >
              {/* Hand-drawn corner sticker */}
              <DoodleDot
                color={p.color === "leaf" ? "green" : p.color === "sun" ? "tomato" : "sun"}
                size={10}
                className="absolute right-5 top-5 wiggle"
              />
              <span
                className={`mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${
                  p.color === "leaf"
                    ? "bg-leaf-100 text-leaf-700"
                    : p.color === "sun"
                    ? "bg-sun-100 text-sun-600"
                    : "bg-sun-100 text-leaf-800"
                }`}
              >
                <p.icon className="h-7 w-7" />
              </span>
              <h3 className="font-display text-2xl font-bold text-leaf-900">
                {p.title}
              </h3>
              <p className="mt-2 text-leaf-900/70">{p.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-leaf-700 group-hover:gap-2 transition-all">
                Learn more <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* ---------------- HOURS CALLOUT ---------------- */}
      <section className="relative overflow-hidden bg-leaf-50 px-4 py-20 sm:px-8 md:px-12">
        <div className="pointer-events-none absolute -top-24 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-sun-200/50 blur-3xl" />
        <div className="relative mx-auto grid max-w-6xl items-center gap-10 md:grid-cols-2">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.22em] text-tomato-500">
              When we're open
            </p>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight text-leaf-900 sm:text-5xl">
              Roll in. We'll <span className="marker-underline italic">make time</span> for you.
            </h2>
            <p className="mt-4 max-w-md text-leaf-900/75">
              Walk-ins are always welcome, but booking ahead gets your bike the
              fastest turnaround. Questions before you come? Just give us a
              call.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/contact#booking"
                data-testid="hours-cta-book"
                className="rounded-full bg-leaf-600 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
              >
                Request Service
              </Link>
              <a
                href={`tel:${(settings.phone || "").replace(/\D/g, "")}`}
                data-testid="hours-cta-call"
                className="rounded-full border-2 border-leaf-900 px-6 py-3 text-sm font-bold text-leaf-900 transition-all hover:-translate-y-0.5 hover:bg-leaf-900 hover:text-white"
              >
                Call Us
              </a>
            </div>
          </div>
          <HoursWidget />
        </div>
      </section>

      {/* ---------------- FAMILY/YOUTH BAND ---------------- */}
      <section className="px-4 py-20 sm:px-8 md:px-12">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] bg-leaf-900 text-white">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="p-8 sm:p-12 md:p-14">
              <p className="text-sm font-black uppercase tracking-[0.22em] text-sun-300">
                Families & Youth
              </p>
              <h2 className="mt-3 font-display text-4xl font-bold leading-tight sm:text-5xl">
                A shop that grows with your kid.
              </h2>
              <p className="mt-5 text-lg text-leaf-50/85">
                From training wheels to first mountain bikes and everything
                after. We offer small-group classes, free quick checks for kids'
                bikes, and cargo e-bike builds to haul the whole crew.
              </p>
              <ul className="mt-7 space-y-3 text-leaf-50/90">
                {[
                  "Free safety checks for kids' bikes",
                  "Teen mechanic apprenticeship days",
                  "Family fit appointments (all bikes, one session)",
                  "Custom cargo e-bike builds",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <Leaf className="mt-1 h-4 w-4 shrink-0 text-sun-300" />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/services"
                data-testid="family-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-sun-400 px-6 py-3 text-sm font-bold text-leaf-900 transition-all hover:-translate-y-0.5 hover:bg-sun-500"
              >
                See all programs <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="relative min-h-[280px] md:min-h-0">
              <img
                src={IMG.community}
                alt="Community group ride"
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
